import { authConfig } from '../config';
import type { TokenRequestBody, TokenResponse } from '../types';

/**
 * Centralised caller for `POST {baseUrl}/{apiVersion}/auth/token`.
 *
 * The only place allowed to talk to the Sync token endpoint. Enforces the
 * contract (JSON `Content-Type`, `cache: 'no-store'`, no sensitive logging)
 * and lets callers pick the desired `grantType` (`authorization_code`,
 * `refresh_token`).
 *
 * Errors are thrown as `Error` with the Sync-provided message when available,
 * without leaking the full response payload.
 */
export async function requestToken(
  body: TokenRequestBody | Record<string, string>,
  context = 'token',
): Promise<TokenResponse> {
  const url = `${authConfig.baseUrl.replace(/\/+$/, '')}/${authConfig.apiVersion}/auth/token`;

  const response = await globalThis.fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    cache: 'no-store',
  });

  if (!response.ok) {
    let detail = `status ${response.status}`;
    try {
      const error = await response.json();
      if (error?.error_description || error?.error) {
        detail = error.error_description || error.error;
      }
    } catch {
      /* non-JSON body */
    }
    throw new Error(`Sync /auth/token (${context}) failed: ${detail}`);
  }

  return (await response.json()) as TokenResponse;
}
